import { BadgeInfoComponent } from '@components/shared/badge-info/badge-info.component';
import { BadgeInfo } from '@models/badge.model';
import { TranslateModule } from '@ngx-translate/core';
import { IBookingStatusModel } from '@services/api/booking-status/booking-status-model.interface';
import { BookingStatusService } from '@services/util/booking-status.service';
import { SelectItemLoaderService, UiSelectItem } from '@services/util/select-item-loader.service';
import { AccordionModule } from 'primeng/accordion';
import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DatePickerModule } from 'primeng/datepicker';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { debounceTime, Observable, Subject, takeUntil } from 'rxjs';
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BookingFilter } from '../../models/booking-filter.model';

@Component({
  selector: 'app-bookings-filters',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    AccordionModule,
    BadgeModule,
    ButtonModule,
    DatePickerModule,
    CardModule,
    SelectModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputTextModule,
    BadgeInfoComponent
  ],
  templateUrl: './bookings-filters.component.html'
})
export class BookingsFiltersComponent implements OnInit, OnDestroy {
  @Input() activeFilter: BookingFilter | undefined;
  @Output() filterChange = new EventEmitter<BookingFilter>();
  @Output() resetFilters = new EventEmitter<void>();

  /**
   * Form group for filter controls
   */
  filterForm!: FormGroup;

  /**
   * Date format for calendar component
   */
  dateFormat = 'yy-mm-dd';

  /**
   * Date filter operator options
   */
  dateOperators = [
    { value: 'exact', label: 'filters.exact' },
    { value: 'before', label: 'filters.before' },
    { value: 'after', label: 'filters.after' },
    { value: 'strictly_before', label: 'filters.strictlyBefore' },
    { value: 'strictly_after', label: 'filters.strictlyAfter' }
  ];

  /**
   * Booking status options
   */
  bookingStatusItems$: Observable<UiSelectItem<IBookingStatusModel>[]>;

  /**
   * Loading state for booking status items
   */
  bookingStatusLoading = true;

  /**
   * Map of booking status badges for UI display
   */
  bookingStatusBadges: Map<number, BadgeInfo> = new Map();

  /**
   * Controls accordion expansion state
   */
  isExpanded = false;

  /**
   * Subject for cleanup
   */
  private destroy$ = new Subject<void>();

  constructor(
    private readonly fb: FormBuilder,
    private readonly selectItemLoader: SelectItemLoaderService,
    private readonly bookingStatusService: BookingStatusService
  ) {
    // Initialize the booking status dropdown items
    this.bookingStatusItems$ = this.selectItemLoader.getBookingStatusSelectItems();
  }

  ngOnInit(): void {
    this.initForm();
    this.setupFormListeners();
    if (this.activeFilter) {
      this.populateForm();
    }
    this.loadBookingStatusItems();
  }

  /**
   * Initialize filter form
   */
  private initForm(): void {
    this.filterForm = this.fb.group({
      guestName: [''],
      status: [null],
      startDate: this.fb.group({
        date: [null],
        operator: ['exact']
      }),
      endDate: this.fb.group({
        date: [null],
        operator: ['exact']
      })
    });
  }

  /**
   * Populate the form with the active filter values
   */
  private populateForm(): void {
    if (!this.activeFilter) return;

    this.filterForm.patchValue({
      guestName: this.activeFilter.guestName || '',
      status: this.activeFilter['bookingStatus.id'] ?? null,
      startDate: {
        date: this.activeFilter.startDate?.date ? new Date(this.activeFilter.startDate.date) : null,
        operator: this.activeFilter.startDate?.operator || 'exact'
      },
      endDate: {
        date: this.activeFilter.endDate?.date ? new Date(this.activeFilter.endDate.date) : null,
        operator: this.activeFilter.endDate?.operator || 'exact'
      }
    });
  }

  /**
   * Clears a specific filter field
   * @param fieldName Name of the filter field to clear
   */
  clearFilterField(fieldName: string): void {
    this.filterForm.get(fieldName)?.setValue('');
  }

  /**
   * Set up form value change listeners
   */
  private setupFormListeners(): void {
    // Listen for form changes and emit filter events
    this.filterForm.valueChanges.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe(() => {});
  }

  /**
   * Load booking status items and create badges
   */
  private loadBookingStatusItems(): void {
    this.bookingStatusLoading = true;

    this.bookingStatusItems$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (items) => {
        // Create badge for each booking status
        items.forEach((item) => {
          if (item.model) {
            const status = item.model;
            this.bookingStatusBadges.set(
              status.id,
              new BadgeInfo(
                status.id,
                item.localizableTitle || '',
                this.bookingStatusService.getStatusColorClasses(status),
                this.bookingStatusService.getStatusIconClasses(status)
              )
            );
          }
        });
        this.bookingStatusLoading = false;
      },
      error: () => {
        this.bookingStatusLoading = false;
      }
    });
  }

  /**
   * Get a booking status badge by ID
   * @param statusId Status ID
   * @returns BadgeInfo instance or undefined if not found
   */
  getStatusBadge(statusId: number): BadgeInfo | undefined {
    return this.bookingStatusBadges.get(statusId);
  }

  /**
   * Apply current filters
   */
  applyFilters(): void {
    if (!this.filterForm) return;

    const formValue = this.filterForm.value;
    const filters: BookingFilter = {};

    // Apply guest name filter if not empty
    if (formValue.guestName) {
      filters.guestName = formValue.guestName.trim();
    }

    // Apply status filter if selected
    if (formValue.status) {
      // For booking status filter, use the model.id as the filter value
      filters['bookingStatus.id'] = formValue.status;
    }

    // Apply start date filter if date is selected
    if (formValue.startDate?.date) {
      filters.startDate = {
        date: formValue.startDate.date,
        operator: formValue.startDate.operator
      };
    }

    // Apply end date filter if date is selected
    if (formValue.endDate?.date) {
      filters.endDate = {
        date: formValue.endDate.date,
        operator: formValue.endDate.operator
      };
    }

    // Emit filter event with active filters
    this.filterChange.emit(filters);
  }

  /**
   * Reset all filters to default values
   */
  resetAllFilters(): void {
    this.filterForm.reset({
      guestName: '',
      status: null,
      startDate: {
        date: null,
        operator: 'exact'
      },
      endDate: {
        date: null,
        operator: 'exact'
      }
    });

    this.filterChange.emit({});
  }

  /**
   * Checks if any filters are currently active
   * @returns true if any filter has a value, false otherwise
   */
  hasActiveFilters(): boolean {
    return this.countActiveFilters() > 0;
  }

  /**
   * Counts the number of active filters
   * @returns number of active filters
   */
  countActiveFilters(): number {
    const formValue = this.filterForm?.value;
    if (!formValue) return 0;

    let count = 0;

    if (formValue.guestName) count++;
    if (formValue.status) count++;
    if (formValue.startDate?.date) count++;
    if (formValue.endDate?.date) count++;

    return count;
  }

  /**
   * Cleanup resources on component destruction
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
